import * as React from 'react'
import { css } from 'styled-components/macro'

/**
 * The underlying DOM element which is rendered by this component.
 */
const Root = 'button'

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  color?: 'primary' | 'secondary'
  startIcon?: React.ReactNode
}

function Button(props: Props, ref: React.ForwardedRef<React.ElementRef<typeof Root>>) {
  const { color = 'primary', startIcon, children, ...rest } = props

  return (
    <Root ref={ref} css={[styles.root, styles.color[color]] as any} {...rest}>
      {startIcon && <span css={styles.startIcon as any}>{startIcon}</span>}
      {children}
    </Root>
  )
}

const styles = {
  root: css`
    display: inline-flex;
    align-items: center;
    vertical-align: middle;

    cursor: pointer;

    border: 0;
    background-color: var(--brandblue-main);
    color: #fff;

    height: 40px;
    padding-inline: 30px;

    font-family: var(--font-sans-serif);
    font-weight: 700;
    font-size: 14px;
    line-height: 1.3;
    letter-spacing: 0.2px;

    transition:
      background-color 0.12s,
      box-shadow 0.12s;
  `,

  color: {
    primary: css`
      background-color: var(--button-background);
      color: var(--site-background);

      &:hover {
        background-color: var(--brandblue-bright-1);
      }
      &:active {
        box-shadow: inset rgb(0 0 0 / 40%) 0 0 14px 3px;
      }

      &:focus-visible {
        outline-offset: 1px;
        outline: var(--button-background) solid 2px;
      }
    `,
    secondary: css`
      background-color: var(--secondary-button-background);
      color: var(--text-color);

      &:hover {
        opacity: 0.7;
      }
      &:active {
        opacity: 0.7;
      }

      &:focus-visible {
        outline-offset: 1px;
        outline: var(--brandblue-main) solid 2px;
      }
    `,
  },

  startIcon: css`
    margin-left: -20px;
    margin-right: 20px;
  `,
}

export default React.forwardRef(Button)
